export interface FontAxisConfig {
  min: number;
  max: number;
  defaultValue: number;
  step: number;
}

export interface VariableFontConfig {
  family: string;
  weightAxis: FontAxisConfig;
  widthAxis?: FontAxisConfig;
}

export interface FontMetadata {
  url: string;
  variableFontConfig?: VariableFontConfig;
}

export type FontSettingsEntry = string | FontMetadata;
export type FontSettingsMap = Record<string, FontSettingsEntry>;

const FONT_WEIGHT_STEP = 50;
const FONT_WIDTH_STEP = 5;

export const STATIC_FONT_WEIGHT_AXIS: FontAxisConfig = {
  min: 100,
  max: 900,
  defaultValue: 400,
  step: FONT_WEIGHT_STEP,
};

const VARIABLE_FONT_CONFIGS: VariableFontConfig[] = [
  {
    family: 'Roboto',
    weightAxis: {
      min: 100,
      max: 900,
      defaultValue: 400,
      step: FONT_WEIGHT_STEP,
    },
    widthAxis: {
      min: 75,
      max: 100,
      defaultValue: 100,
      step: FONT_WIDTH_STEP,
    },
  },
];

const DEFAULT_FONT_WIDTH_AXIS: FontAxisConfig = {
  min: 50,
  max: 200,
  defaultValue: 100,
  step: FONT_WIDTH_STEP,
};

const WOFF2_KNOWN_TAGS: string[] = [
  'cmap', 'head', 'hhea', 'hmtx', 'maxp', 'name', 'OS/2', 'post',
  'cvt ', 'fpgm', 'glyf', 'loca', 'prep', 'CFF ', 'VORG', 'EBDT',
  'EBLC', 'gasp', 'hdmx', 'kern', 'LTSH', 'PCLT', 'VDMX', 'vhea',
  'vmtx', 'BASE', 'GDEF', 'GPOS', 'GSUB', 'EBSC', 'JSTF', 'MATH',
  'CBDT', 'CBLC', 'COLR', 'CPAL', 'SVG ', 'sbix', 'acnt', 'avar',
  'bdat', 'bloc', 'bsln', 'cvar', 'fdsc', 'feat', 'fmtx', 'fvar',
  'gvar', 'hsty', 'just', 'lcar', 'mort', 'morx', 'opbd', 'prop',
  'trak', 'Zapf', 'Silf', 'Glat', 'Gloc', 'Feat', 'Sill',
];

function normalizeFontName(fontName: string | undefined): string {
  return (fontName ?? '').trim().toLowerCase();
}

export function getVariableFontConfig(fontName: string | undefined): VariableFontConfig | null {
  const normalizedName: string = normalizeFontName(fontName);
  if (!normalizedName) {
    return null;
  }

  return (
    VARIABLE_FONT_CONFIGS.find(
      (config: VariableFontConfig) => normalizeFontName(config.family) === normalizedName
    ) ?? null
  );
}

export function clampFontAxisValue(value: unknown, axis: FontAxisConfig): number {
  const numericValue: number = Number(value);
  if (!Number.isFinite(numericValue)) {
    return axis.defaultValue;
  }
  const clampedValue: number = Math.min(axis.max, Math.max(axis.min, numericValue));
  const steppedValue: number = axis.min + Math.round((clampedValue - axis.min) / axis.step) * axis.step;
  return Math.min(axis.max, Math.max(axis.min, Math.round(steppedValue)));
}

function normalizeFontAxisStep(axis: FontAxisConfig, step: number): FontAxisConfig {
  const min: number = Math.ceil(axis.min / step) * step;
  const max: number = Math.floor(axis.max / step) * step;

  if (min > max) {
    const fallbackValue: number = Math.round(axis.defaultValue);
    return {
      min: fallbackValue,
      max: fallbackValue,
      defaultValue: fallbackValue,
      step,
    };
  }

  return {
    min,
    max,
    defaultValue: clampFontAxisValue(axis.defaultValue, { ...axis, min, max, step }),
    step,
  };
}

export function normalizeVariableFontConfigSteps(config: VariableFontConfig): VariableFontConfig {
  return {
    ...config,
    weightAxis: normalizeFontAxisStep(config.weightAxis, FONT_WEIGHT_STEP),
    widthAxis: config.widthAxis
      ? normalizeFontAxisStep(config.widthAxis, FONT_WIDTH_STEP)
      : undefined,
  };
}

export function getFallbackVariableFontConfig(fontName: string | undefined): VariableFontConfig {
  const axisMatch: RegExpMatchArray | null = fontName?.match(/\[([^\]]+)\]/) ?? null;
  const declaredAxes: Set<string> | null = axisMatch
    ? new Set(axisMatch[1].split(',').map((axis: string) => axis.trim().toLowerCase()).filter(Boolean))
    : null;
  const supportsWidth: boolean = declaredAxes
    ? declaredAxes.has('wdth')
    : false;

  return {
    family: fontName ?? '',
    weightAxis: { ...STATIC_FONT_WEIGHT_AXIS },
    widthAxis: supportsWidth ? { ...DEFAULT_FONT_WIDTH_AXIS } : undefined,
  };
}

export function getFontEntryUrl(entry: FontSettingsEntry | undefined): string {
  if (typeof entry === 'string') {
    return entry;
  }
  return entry?.url ?? '';
}

export function getFontEntryVariableConfig(
  entry: FontSettingsEntry | undefined
): VariableFontConfig | undefined {
  if (!entry || typeof entry === 'string') {
    return undefined;
  }
  return entry.variableFontConfig
    ? normalizeVariableFontConfigSteps(entry.variableFontConfig)
    : undefined;
}

export function toFontUrlMap(fonts: FontSettingsMap | undefined): Record<string, string> {
  const fontUrlMap: Record<string, string> = {};
  for (const [fontName, fontEntry] of Object.entries(fonts ?? {})) {
    const url: string = getFontEntryUrl(fontEntry);
    if (url) {
      fontUrlMap[fontName] = url;
    }
  }
  return fontUrlMap;
}

function readTag(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3)
  );
}

function readUIntBase128(view: DataView, offsetRef: { offset: number }): number | null {
  let result = 0;
  for (let i = 0; i < 5; i += 1) {
    if (offsetRef.offset >= view.byteLength) return null;
    const byte: number = view.getUint8(offsetRef.offset);
    offsetRef.offset += 1;
    if (i === 0 && byte === 0x80) return null;
    result = (result << 7) | (byte & 0x7f);
    if ((byte & 0x80) === 0) return result;
  }
  return null;
}

function parseSfntVariableFontConfig(
  fontName: string,
  view: DataView,
  tableOffset: number,
  tableLength: number
): VariableFontConfig {
  if (tableLength < 16 || tableOffset + tableLength > view.byteLength) {
    return getFallbackVariableFontConfig(fontName);
  }

  const axesArrayOffset: number = view.getUint16(tableOffset + 4, false);
  const axisCount: number = view.getUint16(tableOffset + 8, false);
  const axisSize: number = view.getUint16(tableOffset + 10, false);
  const axesStart: number = tableOffset + axesArrayOffset;
  const axesEnd: number = axesStart + axisCount * axisSize;

  if (axisCount === 0 || axisSize < 20 || axesStart < tableOffset || axesEnd > tableOffset + tableLength) {
    return getFallbackVariableFontConfig(fontName);
  }

  let weightAxis: FontAxisConfig | undefined;
  let widthAxis: FontAxisConfig | undefined;

  for (let i = 0; i < axisCount; i += 1) {
    const axisOffset: number = axesStart + i * axisSize;
    const tag: string = readTag(view, axisOffset);
    const min: number = view.getInt32(axisOffset + 4, false) / 65536;
    const defaultValue: number = view.getInt32(axisOffset + 8, false) / 65536;
    const max: number = view.getInt32(axisOffset + 12, false) / 65536;

    if (tag === 'wght') {
      weightAxis = {
        min: Math.round(min),
        max: Math.round(max),
        defaultValue: Math.round(defaultValue),
        step: FONT_WEIGHT_STEP,
      };
    } else if (tag === 'wdth') {
      widthAxis = {
        min: Math.round(min),
        max: Math.round(max),
        defaultValue: Math.round(defaultValue),
        step: FONT_WIDTH_STEP,
      };
    }
  }

  if (!weightAxis) {
    return getFallbackVariableFontConfig(fontName);
  }

  return normalizeVariableFontConfigSteps({
    family: fontName,
    weightAxis,
    widthAxis,
  });
}

function detectSfntVariableFont(fontName: string, view: DataView): VariableFontConfig | null {
  if (view.byteLength < 12) return null;
  const numTables: number = view.getUint16(4, false);
  const directoryEnd: number = 12 + numTables * 16;
  if (directoryEnd > view.byteLength) return null;

  for (let i = 0; i < numTables; i += 1) {
    const entryOffset: number = 12 + i * 16;
    if (readTag(view, entryOffset) !== 'fvar') continue;
    const tableOffset: number = view.getUint32(entryOffset + 8, false);
    const tableLength: number = view.getUint32(entryOffset + 12, false);
    return parseSfntVariableFontConfig(fontName, view, tableOffset, tableLength);
  }

  return null;
}

function detectWoffVariableFont(fontName: string, view: DataView): VariableFontConfig | null {
  if (view.byteLength < 44) return null;
  const numTables: number = view.getUint16(12, false);
  const directoryEnd: number = 44 + numTables * 20;
  if (directoryEnd > view.byteLength) return null;

  for (let i = 0; i < numTables; i += 1) {
    const entryOffset: number = 44 + i * 20;
    if (readTag(view, entryOffset) === 'fvar') {
      return getFallbackVariableFontConfig(fontName);
    }
  }

  return null;
}

function detectWoff2VariableFont(fontName: string, view: DataView): VariableFontConfig | null {
  if (view.byteLength < 48) return null;
  const numTables: number = view.getUint16(12, false);
  const offsetRef = { offset: 48 };

  for (let i = 0; i < numTables; i += 1) {
    if (offsetRef.offset >= view.byteLength) return null;
    const flags: number = view.getUint8(offsetRef.offset);
    offsetRef.offset += 1;
    const tagIndex: number = flags & 0x3f;
    let tag: string | undefined = WOFF2_KNOWN_TAGS[tagIndex];

    if (tagIndex === 0x3f) {
      if (offsetRef.offset + 4 > view.byteLength) return null;
      tag = readTag(view, offsetRef.offset);
      offsetRef.offset += 4;
    }

    if (readUIntBase128(view, offsetRef) === null) return null;
    const hasTransformLength: boolean = tag === 'glyf' || tag === 'loca' || (flags >> 6) !== 0;
    if (hasTransformLength && readUIntBase128(view, offsetRef) === null) return null;
    if (tag === 'fvar') {
      return getFallbackVariableFontConfig(fontName);
    }
  }

  return null;
}

export async function detectVariableFontConfig(file: File, fontName: string): Promise<VariableFontConfig | null> {
  const buffer: ArrayBuffer = await file.arrayBuffer();
  const view: DataView = new DataView(buffer);
  if (view.byteLength < 4) return null;

  const signature: string = readTag(view, 0);
  if (signature === 'wOFF') {
    return detectWoffVariableFont(fontName, view);
  }
  if (signature === 'wOF2') {
    return detectWoff2VariableFont(fontName, view);
  }

  return detectSfntVariableFont(fontName, view);
}
